import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoDatumOzellik } from './FikoDatumOzellik';
import * as THREE from 'three';

/**
 * 🎯 CATIA-style Datum Feature Dialog
 */
@Component({
  selector: 'app-fiko-datum-ozellik',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fiko-datum-ozellik.component.html',
  styleUrls: ['./fiko-datum-ozellik.component.css']
})
export class FikoDatumOzellikComponent implements OnInit, OnDestroy {
  @Input() anaGosterim: FikoThreeJsComponent;
  @Input() pencere: any;

  // Datum settings
  datumHarfi: string = 'A';
  sekil: string = 'rectangle';  // rectangle, circle, triangle
  boyut: number = 12;
  cerceveBoyutu: number = 1.2;
  renk: string = '#00FFFF';

  // Shape options
  sekilSecenekleri = [
    { value: 'rectangle', label: 'Dikdörtgen □' },
    { value: 'circle', label: 'Daire ○' },
    { value: 'triangle', label: 'Üçgen △' }
  ];

  // Point selection
  nokta: THREE.Vector3 | null = null;
  noktaSecimModu: boolean = false;

  // Current annotation
  mevcutDatum: FikoDatumOzellik | null = null;

  // Predefined colors
  renkler = ['#00FFFF', '#FFD700', '#00FF00', '#FF00FF', '#FF4444', '#FFA500'];

  // Sabit boyut - kamera zoom'una göre scale ayarlanır
  sabitBoyut: boolean = true;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Accordion states
  datumAyarlariAcik = true;
  gorunumAyarlariAcik = false;

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.noktaSecimModu) {
      this.noktaSecimModunuKapat();
    }
  }

  /**
   * Start point selection mode
   */
  noktaSec(): void {
    this.noktaSecimModu = true;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
      canvas.addEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Handle point click
   */
  private noktaTiklama = (event: MouseEvent) => {
    const position = this.get3DPositionFromMouse(event);
    if (!position) return;

    this.nokta = position;

    this.noktaSecimModunuKapat();
    this.onizlemeGuncelle();
  };

  /**
   * Close point selection mode
   */
  private noktaSecimModunuKapat(): void {
    this.noktaSecimModu = false;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'default';
      canvas.removeEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Get 3D position from mouse
   */
  private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
    if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) return null;

    const canvas = this.anaGosterim.sunucu.domElement;
    const rect = canvas.getBoundingClientRect();

    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.anaGosterim.aktifKamera);

    const sahne = this.anaGosterim.sahneler?.[0];
    if (!sahne) return null;

    const meshes = sahne.children.filter((obj: any) => obj.isMesh || obj.type === 'Mesh');
    const intersects = raycaster.intersectObjects(meshes, true);

    if (intersects.length > 0) {
      return intersects[0].point.clone();
    }

    // Fallback
    const distance = 5;
    const direction = new THREE.Vector3();
    this.anaGosterim.aktifKamera.getWorldDirection(direction);
    const position = this.anaGosterim.aktifKamera.position.clone();
    position.add(direction.multiplyScalar(distance));

    return position;
  }

  /**
   * Toggle sabit boyut (fixed size regardless of zoom)
   */
  sabitBoyutDegistir(): void {
    if (this.mevcutDatum) {
      this.mevcutDatum.setSabitBoyut(this.sabitBoyut);
      this.anaGosterim?.ciz();
    }
  }

  /**
   * Update preview
   */
  onizlemeGuncelle(): void {
    if (!this.nokta) return;

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne) return;

    // Eski önizleme varsa sil
    if (this.mevcutDatum) {
      sahne.remove(this.mevcutDatum);
      this.mevcutDatum.dispose();
    }

    // Yeni önizleme oluştur
    this.mevcutDatum = new FikoDatumOzellik(
      sahne,
      this.nokta,
      this.datumHarfi.toUpperCase().substring(0, 1)
    );

    sahne.add(this.mevcutDatum);
    this.anaGosterim?.ciz();
  }

  /**
   * Add datum to scene
   */
  ekle(): void {
    if (!this.nokta) {
      alert('Lütfen önce bir nokta seçin!');
      return;
    }

    if (!this.datumHarfi || this.datumHarfi.trim() === '') {
      alert('Lütfen datum harfi girin!');
      return;
    }

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne?.fikoEtiketler) return;

    // Create datum
    const datum = new FikoDatumOzellik(
      sahne,
      this.nokta,
      this.datumHarfi.toUpperCase().substring(0, 1)
    );

    // Add to etiketler group
    const etiket = sahne.fikoEtiketler.ekleDatumOzellik();
    etiket.userData['FikoAnnotation'] = datum;
    datum.parent = etiket;
    sahne.add(datum);

    this.anaGosterim?.ciz();
    this.anaGosterim?.FikoYT?.degisti?.emit();

    // Reset
    this.nokta = null;
    if (this.mevcutDatum) {
      sahne.remove(this.mevcutDatum);
      this.mevcutDatum = null;
    }

    // Auto-increment datum letter
    this.sonrakiDatumHarfi();
  }

  /**
   * Auto-increment datum letter (A -> B -> C -> ... -> Z -> A)
   */
  private sonrakiDatumHarfi(): void {
    const currentCode = this.datumHarfi.charCodeAt(0);
    if (currentCode >= 65 && currentCode < 90) {
      this.datumHarfi = String.fromCharCode(currentCode + 1);
    } else {
      this.datumHarfi = 'A';
    }
  }

  /**
   * Close dialog
   */
  kapat(): void {
    if (this.mevcutDatum) {
      const sahne = this.anaGosterim?.sahneler?.[0];
      if (sahne) {
        sahne.remove(this.mevcutDatum);
        this.mevcutDatum.dispose();
      }
    }
    // pencere.instance → FikoDialogCore instance
    const dialogInstance = this.pencere?.instance ?? this.pencere;
    if (dialogInstance?.kapa) {
      dialogInstance.kapa();
    } else if (dialogInstance?.kapat) {
      dialogInstance.kapat();
    }
  }
}
